import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  DataTable,
  Text,
  Spinner,
  Badge,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Tooltip,
  Icon,
  Tooltip as ToolTip,
} from '@shopify/polaris';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { InfoIcon } from '@shopify/polaris-icons';
import axios from 'axios';
import { format, parseISO } from 'date-fns';


function AllPostTable({shopOrigin, apiKey}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get parameters from URL
  // const shopOrigin = searchParams.get('shop');
  const dataType = searchParams.get('type'); // 'sessions', 'engagement', or 'products'
  const since = searchParams.get('since');
  const until = searchParams.get('until');
  const limit = searchParams.get('limit') || 'All'; // Get limit from URL, default to 'All'
  
  // State
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [banner, setBanner] = useState({ show: false, message: '', status: 'success' });


  // Fetch data based on type
  useEffect(() => {
    if (!shopOrigin || !dataType) {
      setBanner({
        show: true,
        message: 'Missing required parameters',
        status: 'critical',
      });
      setLoading(false);
      return;
    }


    fetchData();
  }, [shopOrigin, dataType, since, until, limit]);

  // Helper function to create bold heading
  const createBoldHeading = (text) => {
    return <span style={{ fontWeight: 600, fontSize: '13px' }}>{text}</span>;
  };

  // Helper function to create header with tooltip
  const createHeaderWithTooltip = (label, tooltipContent) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>{label}</span>
        <Tooltip content={tooltipContent} dismissOnMouseOut>
          <Icon source={InfoIcon} tone="base" />
        </Tooltip>
      </div>
    );
  };

  // Add this function before renderCard
  const createHeaderWithTooltip_session = (label, tooltipContent) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginRight: '-6px' }}>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>{label}</span>
        <ToolTip content={tooltipContent} dismissOnMouseOut>
          <Icon source={InfoIcon} tone="base" />
        </ToolTip>
      </div>
    );
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      let response;
      let rows = [];


      switch (dataType) {
        case 'sessions':
          response = await axios.get(
            `/analytics/top-posts/?shop=${shopOrigin}&since=${since}&until=${until}&limit=${limit}`
          );
          // console.log('Response from analytics/top-posts/:', response.data);
          if (!Array.isArray(response.data)) {
            throw new Error('Invalid response format');
          }
          rows = response.data.map((post) => [
            // ✅ Make article title clickable
            <Link
              className="Polaris-Text--root Polaris-Text--bodySm"
              to={`/edit-blogs?shop=${encodeURIComponent(shopOrigin)}&article_id=${encodeURIComponent(post.article_id)}`}
              style={{ color: '#303030', textDecoration: 'none', cursor: 'pointer' }}
            >
              {/* // <Text as="span"> */}
                {post.title}
              {/* </Text>, */}
            </Link>,
            <InlineStack gap="150" blockAlign="center" align="end">
              <Text as="span">{post.views?.toString() || '0'}</Text>
            </InlineStack>,
          ]);
          break;


        case 'engagement':
          response = await axios.get(
            `/analytics/engagement/?shop=${shopOrigin}&since=${since}&until=${until}&limit=${limit}`
          );
          // ✅ Fix: Access per_article array
          const postsWithEngagement = response.data.per_article
            .map((post) => {
              const engagementPercent = Math.min(
                Math.round((post.avg_seconds / 300) * 100),
                100
              );
              return {
                title: post.title || post.handle || 'Untitled',
                engagement: engagementPercent,
                avg_seconds: post.avg_seconds,
                article_id: post.article_id, // ✅ Store article_id
              };
            })
            .sort((a, b) => b.engagement - a.engagement);


          rows = postsWithEngagement.map((post) => [
            // ✅ Make article title clickable
            <Link
              className="Polaris-Text--root Polaris-Text--bodySm"
              to={`/edit-blogs?shop=${encodeURIComponent(shopOrigin)}&article_id=${encodeURIComponent(post.article_id)}`}
              style={{ color: '#303030', textDecoration: 'none', cursor: 'pointer' }}
            >
              {/* <Text as="span"> */}
                {post.title}
              {/* </Text>, */}
            </Link>,
            <Text as="span">{post.engagement}%</Text>,
          ]);
          break;


        case 'products':
          response = await axios.get(
            `/analytics/top-clicked-products/?shop=${shopOrigin}&since=${since}&until=${until}`
          );
          rows = response.data.map((product) => [
            product.title || 'Untitled Product',
            <Text as="span">{product.clicks?.toString() || '0'}</Text>,
          ]);
          break;


        default:
          throw new Error('Invalid data type');
      }


      setData(rows);
    } catch (error) {
      console.error('Error fetching data:', error);
      setBanner({
        show: true,
        message: `Failed to load ${dataType} data`,
        status: 'critical',
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };



  const getTitle = () => {
    switch (dataType) {
      case 'sessions':
        return 'All Blog Posts by Sessions';
      case 'engagement':
        return 'All Blog Posts by Engagement';
      case 'products':
        return 'All Clicked Products';
      default:
        return 'All Posts';
    }
  };


  const getHeadings = () => {
    switch (dataType) {
      case 'sessions':
        return [
          createBoldHeading('Article Title'),
          createHeaderWithTooltip_session('Views', 'Total number of page views this article has received')
        ];
      case 'engagement':
        return [
          createBoldHeading('Article Title'),
          createHeaderWithTooltip('Engagement', 'Engagement rate based on average time spent reading the article')
        ];
      case 'products':
        return [
          createBoldHeading('Product Title'),
          createHeaderWithTooltip('Clicks', 'Total number of clicks on this product from blog articles')
        ];
      default:
        return ['Title', 'Value'];
    }
  };


  const getDateRange = () => {
    if (!since || !until) return '';
    try {
      const sinceDate = format(parseISO(since), 'MMM dd, yyyy');
      const untilDate = format(parseISO(until), 'MMM dd, yyyy');
      return `${sinceDate} - ${untilDate}`;
    } catch {
      return '';
    }
  };


  return (
    <Page
      title={getTitle()}
      // subtitle={getDateRange()}
      backAction={{
        content: 'Dashboard',
        onAction: () => navigate(`/dashboard?shop=${encodeURIComponent(shopOrigin)}`),
      }}
    >
      {banner.show && (
        <Banner
          status={banner.status}
          onDismiss={() => setBanner({ show: false, message: '', status: 'success' })}
        >
          {banner.message}
        </Banner>
      )}


      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {getTitle()}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Total: {data.length} items
            </Text>
          </InlineStack>


          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Spinner size="large" />
            </div>
          ) : data.length > 0 ? (
            <DataTable
              columnContentTypes={['text', 'numeric']}
              headings={getHeadings()}
              rows={data}
              hoverable
            />
          ) : (
            <Text as="p" tone="subdued" alignment="center">
              No data available for the selected period.
            </Text>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}


export default AllPostTable;
